import { CurveType, PropertyType } from '@beatflyer/shaders-lib';

const getLinearNumberValue = (
  p: number,
  minValue: number | undefined,
  maxValue: number | undefined
): number => {
  if (minValue === undefined || maxValue === undefined) {
    return p;
  }
  const range = maxValue - minValue;
  return minValue + range * p;
};
const getLinearNumberPosition = (
  v: number,
  minValue: number | undefined,
  maxValue: number | undefined
): number => {
  if (minValue === undefined || maxValue === undefined) {
    return v;
  }
  const range = maxValue - minValue;
  return (v - minValue) / range;
};

const getCircularNumberValue = (
  p: number,
  minValue: number | undefined,
  maxValue: number | undefined
): number => {
  if (minValue === undefined || maxValue === undefined) {
    return p;
  }
  if (p === 0) {
    return minValue;
  }
  if (p === 1) {
    return maxValue;
  }

  const radius = maxValue - minValue;
  const getValue = (po: number): number => {
    return (1 - Math.sqrt(1 - po * po)) * radius;
  };

  if (minValue < 0 && maxValue > 0) {
    // dual
    if (p === 0.5) {
      return 0;
    }
    if (p > 0.5) {
      // positive
      return getValue((p - 0.5) * 2);
    } else {
      return -getValue((0.5 - p) * 2);
    }
  }
  // single
  return minValue + getValue(p);
};
const getCircularNumberPosition = (
  v: number,
  minValue: number | undefined,
  maxValue: number | undefined
): number => {
  if (minValue === undefined || maxValue === undefined) {
    return v;
  }

  if (v === minValue) {
    return 0;
  }
  if (v === maxValue) {
    return 1;
  }

  const radius = maxValue - minValue;
  const getPosition = (vo: number): number => {
    vo /= radius;
    vo = 1 - vo;
    return Math.sqrt(1 - vo * vo);
  };

  if (minValue < 0 && maxValue > 0) {
    // dual
    if (v === 0) {
      return 0.5;
    }
    if (v > 0) {
      // positive
      return 0.5 + getPosition(v) * 0.5;
    } else {
      return 0.5 - getPosition(-v) * 0.5;
    }
  }
  // single
  return getPosition(v - minValue);
};

const getLogarithmicNumberValue = (
  p: number,
  minValue: number | undefined,
  maxValue: number | undefined
): number => {
  if (minValue === undefined || maxValue === undefined) {
    return p;
  }

  if (p === 0) {
    return minValue;
  }
  if (p === 1) {
    return maxValue;
  }

  const scale = Math.log(maxValue * 1000);
  const getValue = (po: number): number => {
    return Math.exp(po * scale) * 0.001;
  };

  if (minValue < 0 && maxValue > 0) {
    // dual
    if (p === 0.5) {
      return 0;
    }
    if (p > 0.5) {
      // positive
      return getValue((p - 0.5) * 2);
    } else {
      return -getValue((0.5 - p) * 2);
    }
  }
  // single
  return minValue + getValue(p);
};
const getLogarithmicNumberPosition = (
  v: number,
  minValue: number | undefined,
  maxValue: number | undefined
): number => {
  if (minValue === undefined || maxValue === undefined) {
    return v;
  }

  if (v === minValue) {
    return 0;
  }
  if (v === maxValue) {
    return 1;
  }

  const scale = Math.log(maxValue * 1000);
  const getPosition = (vo: number): number => {
    return Math.log(vo * 1000) / scale;
  };

  if (minValue < 0 && maxValue > 0) {
    // dual
    if (v === 0) {
      return 0.5;
    }
    if (v > 0) {
      // positive
      return 0.5 + getPosition(v) * 0.5;
    } else {
      return 0.5 - getPosition(-v) * 0.5;
    }
  }
  // single
  return getPosition(v - minValue);
};

export interface CustomProperty {
  name: string;
  type: PropertyType | string;
  value: any;
  maxValue?: number;
  minValue?: number;
  options?: any[];
  curve?: string;
  dismissOnSelect?: boolean;
  formatValue?: (value: any) => string;
}

export const getPropertyPositionWithValue = (
  p: CustomProperty,
  v: number
): number => {
  const { curve, minValue, maxValue } = p;
  switch (curve) {
    case CurveType.LOGARITHMIC:
      return getLogarithmicNumberPosition(v, minValue, maxValue);
    case CurveType.CIRCULAR:
      return getCircularNumberPosition(v, minValue, maxValue);
    default:
      return getLinearNumberPosition(v, minValue, maxValue);
  }
};

export const getPropertyValueWithPosition = (
  p: CustomProperty,
  pos: number
): number => {
  const { curve, minValue, maxValue } = p;
  switch (curve) {
    case CurveType.LOGARITHMIC:
      return getLogarithmicNumberValue(pos, minValue, maxValue);
    case CurveType.CIRCULAR:
      return getCircularNumberValue(pos, minValue, maxValue);
    default:
      return getLinearNumberValue(pos, minValue, maxValue);
  }
};



// WEBPACK FOOTER //
// ./src/data/CustomProperty.ts